import { Serializer } from "@shared/mirage/helpers/Serializer";
import { memo, useCallback, useEffect } from "react";
import EnforcementAppealsSearchState from "./EnforcementAppealsSearchState";
import EnforcementAppealsSimpleSearch from "./EnforcementAppealsSimpleSearch";
import EnforcementAppealsService from "@publicPortal/services/EnforcementAppealsService";
import { EntityTableStateStorage } from "@mirage/components/entityTable/EntityTableStateStorage";
import PublicPortalState from "@publicPortal/utils/InitialState";
import { EnforcementAppealsFilter } from "@shared/publicportal/contracts/EnforcementAppealsFilter";
import { atom, useRecoilState } from "recoil";
import SearchTypeSwitchingTabs from "@publicPortal/components/advancedSearch/SearchTypeSwitchingTabs";
import SearchActions from "../SearchActions";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { SearchType } from "@shared/publicportal/contracts/Search";
import EnforcementAppealsAdvancedSearch from "@publicPortal/components/advancedSearch/EnforcementAppealsAdvancedSearch";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { PublicPortalMapSearch } from "@publicPortal/components/mapSearch/PublicPortalMapSearch";

const service = new EnforcementAppealsService();
const pp = new PublicPortalRoutingService();
const { initialSearchState, initialFilterState } = PublicPortalState.enforcementAppealInitialSearchState();
const enforcementAppealsSearchState = atom({
    key: "enforcementAppealsSearchState",
    default: initialSearchState,
});

const EnforcementAppealsSearch = memo(() => {
    const [state, setState] = useRecoilState<EnforcementAppealsSearchState>(enforcementAppealsSearchState);
    const navigate = useStableNavigate();
    const setStateModifier = useCallback(
        (modifier: Partial<EnforcementAppealsFilter>) => {
            setState((prev) => {
                return { ...prev, filter: { ...prev.filter, ...modifier } };
            });
        },
        [setState]
    );
    const onSearchTypeChanged = useCallback(
        (newSearchType: SearchType) => {
            setState((prev) => {
                return { ...prev, searchType: newSearchType };
            });
        },
        [setState]
    );

    useEffect(() => {
        const getFilteringLists = async () => {
            const filter = await service.getFilteringLists();
            setState((prev) => {
                return { ...prev, decisionTypes: filter?.decisionTypes || [], parishes: filter?.parishes || [], wards: filter?.wards || [] };
            });
        };
        getFilteringLists();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const prepareFilter = useCallback(() => {
        switch (state.searchType) {
            case "Simple": {
                return {
                    searchType: state.searchType,
                    appealRef: state.filter.appealRef,
                    noticeRef: state.filter.noticeRef,
                    inspectorateRef: state.filter.inspectorateRef,
                };
            }
            case "Advanced": {
                return { ...state.filter, searchType: state.searchType };
            }
            case "Map": {
                return { searchType: state.searchType, geometry: state.filter.geometry };
            }
            default:
                return null;
        }
    }, [state.filter, state.searchType]);
    const onClear = useCallback(() => {
        setState((prev) => {
            return {
                ...prev,
                filter: {
                    ...initialFilterState,
                },
            };
        });
    }, [setState]);
    const onSubmit = useCallback(() => {
        const filter = prepareFilter();
        const searchParams = Serializer.toQueryParamsNotEmptyOnly(filter);
        EntityTableStateStorage.clear("enforcementappeals");
        navigate(`${pp.SearchRoute("EnforcementAppeals", searchParams)}`);
    }, [navigate, prepareFilter]);

    return (
        <>
            <SearchTypeSwitchingTabs searchType={state.searchType} onSearchTypeChanged={onSearchTypeChanged} />
            {getForm()}
            <SearchActions onCancelHandler={onClear} onSubmitHandler={onSubmit} />
        </>
    );

    function getForm() {
        switch (state.searchType) {
            case "Simple": {
                return <EnforcementAppealsSimpleSearch state={state} setter={setStateModifier} />;
            }
            case "Advanced": {
                return <EnforcementAppealsAdvancedSearch state={state} setter={setStateModifier} />;
            }
            case "Map": {
                return <PublicPortalMapSearch setStateModifier={setStateModifier} geometry={state.filter.geometry} />;
            }
            default:
                return null;
        }
    }
});

export default EnforcementAppealsSearch;
